/**
 * 「N月度」「N月」「YYYY年N月」といった月表記を翌月へシフトするユーティリティ。
 *
 * 毎月決まって請求する案件（例「5月度保守費用」）を複製する際、月表記だけを
 * 翌月（5月度→6月度）へ自動更新するために使用する。受注の案件名と、紐づく見積の
 * 明細項目名・摘要の両方に適用される。
 *
 * - 12月度 → 1月度（年付きの場合は翌年へ繰り上がる）
 * - 全角数字（５月度）も対象。出力は半角に正規化する。
 * - 1〜12 の範囲外（例 13月）は月表記とみなさず変更しない。
 */

import { Timestamp } from 'firebase/firestore';

export interface MonthShiftResult {
  text: string;
  shifted: boolean;
}

const FULLWIDTH_DIGITS = /[０-９]/g;

function toHalfWidthDigits(input: string): string {
  return input.replace(FULLWIDTH_DIGITS, (ch) =>
    String.fromCharCode(ch.charCodeAt(0) - 0xfee0)
  );
}

// 「YYYY年」を任意の前置として、続く「N月」を捕捉する。「度」は任意。
// 数字は全角・半角どちらも許容する。
const MONTH_TOKEN = /(?:([0-9０-９]{4})年)?([0-9０-９]{1,2})月(度)?/g;

/**
 * 文字列中の月表記をすべて翌月へシフトする。
 * @param input 対象文字列（null/undefined は空文字として扱う）
 * @returns シフト後の文字列と、1件でも変更があったかのフラグ
 */
export function shiftMonthTokens(input: string | null | undefined): MonthShiftResult {
  if (!input) return { text: '', shifted: false };

  let shifted = false;
  const text = input.replace(
    MONTH_TOKEN,
    (whole, yearStr: string | undefined, monthStr: string, doSuffix: string | undefined) => {
      const month = Number(toHalfWidthDigits(monthStr));
      if (!Number.isInteger(month) || month < 1 || month > 12) return whole;

      const rollsOver = month === 12;
      const nextMonth = rollsOver ? 1 : month + 1;
      shifted = true;

      const suffix = `${nextMonth}月${doSuffix ?? ''}`;
      if (yearStr === undefined) return suffix;

      const year = Number(toHalfWidthDigits(yearStr));
      const nextYear = rollsOver ? year + 1 : year;
      return `${nextYear}年${suffix}`;
    }
  );

  return { text, shifted };
}

/**
 * 翌月複製用の件名/タイトルを生成する。
 * 月表記があれば翌月へシフトし、無ければ識別のため「 コピー」を付与する。
 */
export function nextMonthTitle(title: string | null | undefined): string {
  const r = shiftMonthTokens(title);
  if (r.shifted) return r.text;
  const base = title ?? '';
  return base ? `${base} コピー` : 'コピー';
}

/**
 * Timestamp を1ヶ月後へシフトする（年跨ぎは Date.setMonth が自動処理）。
 * null はそのまま返す。翌月複製時の納期・有効期限の更新に使用する。
 */
export function shiftTimestampOneMonth(ts: Timestamp | null): Timestamp | null {
  if (!ts) return ts;
  const d = new Date(ts.seconds * 1000);
  d.setMonth(d.getMonth() + 1);
  return Timestamp.fromDate(d);
}
